import { CreatedProjects, LandholdingDetails, ProjectDetailsStepState, ProjectRow } from "../../../../../models";
import { getActiveProjectRows } from "./projectRowSelectors";

/*
  One api call the journey will make for its rows. A landholding goes through createGroup because
  that is the only call that groups its members; every standalone row goes through the one bulk
  call. The key names the request across a save and its retries - what a partial save recorded
  under it is what a retry skips.
*/
export type ProjectCreateRequest =
  | {
      kind: "group";
      key: string;
      rows: ProjectRow[];
      landholding: LandholdingDetails;
      // Set on a landholding read from an uploaded file: the group's own errors (its name, its
      // reference, a reference already taken) have no row to sit against and no form of their own,
      // so they are stamped onto this row and shown in the parsed table under the landholding's name.
      landholdingErrorRowKey?: string;
    }
  | { kind: "bulk"; key: "bulk"; rows: ProjectRow[] };

export const MANUAL_LANDHOLDING_REQUEST_KEY = "landholding";

const normalise = (value: string | null | undefined): string => (value ?? "").trim().toLowerCase();

// A row is under a landholding when any of its three landholding cells says so. A row with a name
// and no reference is still sent as a landholding, so that the api can say what it is missing,
// rather than quietly becoming a standalone project.
const isLandholdingRow = (row: ProjectRow): boolean =>
  !!normalise(row.landholdingName) || !!normalise(row.landholdingReference) || !!normalise(row.landholdingReferenceId);

/*
  Two rows are the same landholding when they carry the same kind of reference and the same
  reference, compared case-insensitively - the reference is what identifies a landholding to the
  api (ValidateGroupReferenceIsUnique), the name is only carried along. Rows with no reference at
  all fall back to grouping by name, so that two unrelated landholdings both missing a reference are
  reported as two, each under its own name, rather than as one.
*/
const uploadGroupKey = (row: ProjectRow): string => {
  const kind = normalise(row.landholdingReference);
  const id = normalise(row.landholdingReferenceId);
  return kind || id ? `upload:${kind}|${id}` : `upload:name:${normalise(row.landholdingName)}`;
};

// The landholding as its first row describes it: the name from that row, the reference as typed.
// The sheet has no landholding-level location columns, and the api requires none, so those are
// left for the manage-landholding page.
const uploadLandholding = (row: ProjectRow): LandholdingDetails => ({
  name: (row.landholdingName ?? "").trim(),
  reference: (row.landholdingReference ?? "").trim() || null,
  referenceId: (row.landholdingReferenceId ?? "").trim(),
  region: "",
  latitude: null,
  longitude: null,
  gridReference: null,
  countryCode: null,
});

// Requests come out in the order the rows first appear, so error lists and the parsed table read
// in the same order as the file.
const buildUploadRequests = (rows: ProjectRow[]): ProjectCreateRequest[] => {
  const groups = new Map<string, Extract<ProjectCreateRequest, { kind: "group" }>>();
  const standalone: ProjectRow[] = [];

  rows.forEach((row) => {
    if (!isLandholdingRow(row)) {
      standalone.push(row);
      return;
    }

    const key = uploadGroupKey(row);
    const existing = groups.get(key);

    if (existing) {
      existing.rows.push(row);
      return;
    }

    groups.set(key, {
      kind: "group",
      key,
      rows: [row],
      landholding: uploadLandholding(row),
      landholdingErrorRowKey: row.key,
    });
  });

  const requests: ProjectCreateRequest[] = Array.from(groups.values());
  if (standalone.length > 0) requests.push({ kind: "bulk", key: "bulk", rows: standalone });

  return requests;
};

export const buildCreateRequests = (details: ProjectDetailsStepState): ProjectCreateRequest[] => {
  const rows = getActiveProjectRows(details);

  if (details.entryMethod === "upload") return buildUploadRequests(rows);

  if (details.isLandholding) {
    return [{ kind: "group", key: MANUAL_LANDHOLDING_REQUEST_KEY, rows, landholding: details.data.landholding }];
  }

  return [{ kind: "bulk", key: "bulk", rows }];
};

// The landholding each row will be created under, by row key - read from the same requests the
// save sends, so the access step names exactly the groups the create will write. A standalone row
// has no entry.
export const getLandholdingNameByRowKey = (details: ProjectDetailsStepState): Record<string, string> =>
  Object.fromEntries(
    buildCreateRequests(details).flatMap((request) =>
      request.kind === "group"
        ? request.rows.map((row) => [row.key, request.landholding.name.trim() || "Unnamed landholding"])
        : []
    )
  );

/*
  What a request still has to write, given what an earlier save already did. A group is written
  with all of its members in one transaction, so a recorded group uuid means the whole request is
  done; the bulk call is one transaction too, but its rows are still checked one by one against
  the uuids the api accepted, so a row that exists is never posted again (BR-02888).
*/
export const getPendingRows = (request: ProjectCreateRequest, created: CreatedProjects | null): ProjectRow[] => {
  if (!created) return request.rows;

  if (request.kind === "group") return created.groupUuidByKey[request.key] ? [] : request.rows;

  return request.rows.filter((row) => !created.projectUuidByKey[row.key]);
};

// Nothing left to send: every request is either written as a whole or has no rows still missing.
export const hasPendingRequests = (details: ProjectDetailsStepState, created: CreatedProjects | null): boolean =>
  buildCreateRequests(details).some((request) => getPendingRows(request, created).length > 0);

// A save wrote some of the requests and was refused on the rest - only an upload of several
// landholdings can get here, being several creates.
export const isPartiallyCreated = (details: ProjectDetailsStepState, created: CreatedProjects | null): boolean =>
  created !== null && hasPendingRequests(details, created);

/*
  A file replaced after a save wrote only some of its requests. What was written stays as it was
  sent - its rows keep the uuids the api accepted and the access step's assignments point at - and
  the new file supplies only the requests still missing. A row of the new file that falls under a
  request already written is dropped rather than sent: the request key is the landholding's
  reference (or "bulk"), so it would be the same landholding posted again (BR-02888).
*/
export const mergeReplacedUploadRows = (
  details: ProjectDetailsStepState,
  replacementRows: ProjectRow[],
  created: CreatedProjects | null
): ProjectRow[] => {
  if (!created) return replacementRows;

  const writtenRequests = buildCreateRequests(details).filter(
    (request) => getPendingRows(request, created).length === 0
  );
  if (writtenRequests.length === 0) return replacementRows;

  const writtenKeys = new Set(writtenRequests.map((request) => request.key));
  const replacements = buildCreateRequests({
    ...details,
    entryMethod: "upload",
    data: { ...details.data, rows: replacementRows },
  })
    .filter((request) => !writtenKeys.has(request.key))
    .flatMap((request) => request.rows);
  const stillSent = new Set(replacements.map((row) => row.key));

  return [
    ...writtenRequests.flatMap((request) => request.rows),
    ...replacementRows.filter((row) => stillSent.has(row.key)),
  ];
};
